import { useState } from 'react'
import { ChevronLeft, ChevronRight, Clock, MapPin, User } from 'lucide-react'
import { useSessionStore } from '@/stores/sessionStore'
import { mockClients } from '@/data'
import type { Session } from '@/types'

interface CalendarViewProps {
  onSessionClick: (session: Session) => void
}

export function CalendarView({ onSessionClick }: CalendarViewProps) {
  const { selectedDate, setSelectedDate, sessions } = useSessionStore()
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate))

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    
    // Get the first Monday of the calendar grid
    const startDate = new Date(firstDay)
    const firstDayOfWeek = firstDay.getDay()
    const mondayOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1
    startDate.setDate(firstDay.getDate() - mondayOffset)
    
    const days = []
    const totalDays = 42 // 6 weeks * 7 days
    
    for (let i = 0; i < totalDays; i++) {
      const day = new Date(startDate)
      day.setDate(startDate.getDate() + i)
      days.push(day)
    }
    
    return days
  }

  const getSessionsForDate = (date: Date) => {
    const targetDate = new Date(date)
    targetDate.setHours(0, 0, 0, 0)
    
    return sessions.filter(session => {
      const sessionDate = new Date(session.date)
      sessionDate.setHours(0, 0, 0, 0)
      return sessionDate.getTime() === targetDate.getTime()
    }).sort((a, b) => a.startTime.localeCompare(b.startTime))
  }

  const getClientName = (clientId: string) => {
    const client = mockClients.find(c => c.id === clientId)
    return client?.name || 'Unknown Client'
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth)
    if (direction === 'prev') {
      newMonth.setMonth(currentMonth.getMonth() - 1)
    } else {
      newMonth.setMonth(currentMonth.getMonth() + 1)
    }
    setCurrentMonth(newMonth)
  }

  const isToday = (date: Date) => {
    const today = new Date()
    return date.toDateString() === today.toDateString()
  }

  const isSelectedDate = (date: Date) => {
    return date.toDateString() === selectedDate.toDateString()
  }

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentMonth.getMonth()
  }

  const getSessionStatusColor = (status: Session['status']) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-500'
      case 'in-progress':
        return 'bg-yellow-500'
      case 'completed':
        return 'bg-green-500'
      case 'cancelled':
        return 'bg-red-500'
      case 'no-show':
        return 'bg-gray-500'
      default:
        return 'bg-gray-400'
    }
  }

  const days = getDaysInMonth(currentMonth)

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <button
          onClick={() => navigateMonth('prev')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <h2 className="text-xl font-semibold text-gray-900">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h2>
        
        <button
          onClick={() => navigateMonth('next')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Days of Week Header */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {daysOfWeek.map(day => (
          <div key={day} className="p-3 text-center text-sm font-medium text-gray-500">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {days.map((date, index) => {
          const dateSessions = getSessionsForDate(date)
          const isCurrentMonthDate = isCurrentMonth(date)
          
          return (
            <div
              key={index}
              className={`min-h-[120px] p-2 border-r border-b border-gray-100 ${
                isCurrentMonthDate ? 'bg-white' : 'bg-gray-50'
              } ${isSelectedDate(date) ? 'bg-blue-50' : ''}`}
            >
              {/* Date Number */}
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={() => setSelectedDate(date)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    isToday(date)
                      ? 'bg-blue-600 text-white'
                      : isSelectedDate(date)
                      ? 'bg-blue-100 text-blue-700'
                      : isCurrentMonthDate
                      ? 'text-gray-900 hover:bg-gray-100'
                      : 'text-gray-400'
                  }`}
                >
                  {date.getDate()}
                </button>
                
                {dateSessions.length > 0 && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {dateSessions.length}
                  </span>
                )}
              </div>

              {/* Sessions */}
              <div className="space-y-1">
                {dateSessions.slice(0, 3).map(session => (
                  <button
                    key={session.id}
                    onClick={() => onSessionClick(session)}
                    className={`w-full text-left p-2 rounded text-xs hover:bg-opacity-80 transition-colors ${getSessionStatusColor(session.status)} text-white`}
                  >
                    <div className="flex items-center gap-1 mb-1">
                      <Clock className="w-3 h-3" />
                      <span>{session.startTime}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-1">
                      <User className="w-3 h-3" />
                      <span className="truncate">{getClientName(session.clientId)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate capitalize">{session.location?.type || 'TBD'}</span>
                    </div>
                  </button>
                ))}
                
                {dateSessions.length > 3 && (
                  <div className="text-xs text-gray-500 text-center py-1">
                    +{dateSessions.length - 3} more
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}